"use client";

import * as React from "react";
import { useTranslations } from "next-intl";
import { toast } from "sonner";
import { IconCarOff, IconLoader2, IconUserCheck } from "@tabler/icons-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { assignTransfer, type Driver, type Transfer } from "@/lib/logistics-api";
import { normalizePickupTime } from "@/lib/logistics-day-sheet";

/**
 * Logistics — the assign / re-assign popover.
 *
 * Lists the ACTIVE drivers with how many rides each already holds on the board
 * day, so the coordinator picks by load rather than alphabetically. A pickup time
 * is REQUIRED when the ride has none (an auto-suggested ride the coordinator is
 * scheduling for the first time) — the driver buttons stay disabled until a time
 * is entered, which is the whole reason assign and time-set are one PATCH.
 *
 * Mutates directly (`PATCH /logistics/transfers/{id}/assign`), then hands control
 * back to the board via `onAssigned` for the imperative reload — no React Query.
 */

const minutesOf = (time: string) => {
  const [hours, minutes] = time.split(":").map(Number);
  return hours * 60 + minutes;
};

/**
 * Does this driver's standing shift cover the pickup? Mirrors
 * DriverShift::covers() — start inclusive, end exclusive, an end before the
 * start wraps midnight. Null when there is no shift or no time to judge.
 */
export function shiftCovers(driver: Driver, time: string | null): boolean | null {
  if (!driver.shift || !time) return null;
  const at = minutesOf(time);
  const start = minutesOf(driver.shift.starts_at);
  const end = minutesOf(driver.shift.ends_at);
  return start <= end ? at >= start && at < end : at >= start || at < end;
}

export interface AssignPopoverDriverOption {
  driver: Driver;
  /** Rides this driver already holds on the board day (cancelled excluded). */
  rideCount: number;
}

export interface AssignPopoverProps {
  /** Only these three fields are read — so a board that is not the logistics
   *  board (the desk's Needs-booking table) can hand over a ride summary. */
  transfer: Pick<Transfer, "id" | "pickup_time" | "driver_id">;
  /** Active drivers only — a retired driver must never receive a new ride. */
  drivers: AssignPopoverDriverOption[];
  /** Reload the board after a successful assign / unassign. */
  onAssigned: () => void | Promise<void>;
  /** Trigger label ("Assign" in the unassigned stack, "Schedule" above it). */
  label: string;
  /** Rendered instead of the default outline button when supplied. */
  trigger?: React.ReactNode;
  className?: string;
}

export function AssignPopover({
  transfer,
  drivers,
  onAssigned,
  label,
  trigger,
  className,
}: AssignPopoverProps) {
  const t = useTranslations("logistics");

  const [open, setOpen] = React.useState(false);
  const [time, setTime] = React.useState(
    () => normalizePickupTime(transfer.pickup_time) ?? "",
  );
  const [pendingId, setPendingId] = React.useState<string | null>(null);
  const [showTimeError, setShowTimeError] = React.useState(false);

  // Re-seed whenever the popover opens so a board reload behind it can't leave a
  // stale time in the field.
  React.useEffect(() => {
    if (open) {
      setTime(normalizePickupTime(transfer.pickup_time) ?? "");
      setShowTimeError(false);
    }
  }, [open, transfer.pickup_time]);

  const requiresTime = transfer.pickup_time === null;
  const timeMissing = requiresTime && !time;

  const submit = async (driverId: string | null, driverName?: string) => {
    if (driverId !== null && timeMissing) {
      setShowTimeError(true);
      return;
    }
    setPendingId(driverId ?? "__unassign__");
    try {
      await assignTransfer(transfer.id, {
        driver_id: driverId,
        // Only send a time when it actually changed / was newly supplied — an
        // unchanged 'HH:mm' round-trip is harmless, but omitting it keeps the
        // PATCH minimal and avoids re-writing a 'HH:mm:ss' value as 'HH:mm'.
        ...(time && time !== normalizePickupTime(transfer.pickup_time)
          ? { pickup_time: time }
          : {}),
      });
      setOpen(false);
      await onAssigned();
      toast.success(
        driverId === null
          ? t("toast.unassigned")
          : t("toast.assigned", { driver: driverName ?? "" }),
      );
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      console.error("Failed to assign transfer:", error);
      toast.error(err.response?.data?.message || t("toast.assignFailed"));
    } finally {
      setPendingId(null);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild onClick={(event) => event.stopPropagation()}>
        {trigger ?? (
          <Button variant="outline" size="sm" className={cn("h-7 px-2 text-xs", className)}>
            {label}
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-72 p-3"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor={`assign-time-${transfer.id}`} className="text-xs text-muted-foreground">
              {requiresTime ? t("form.pickupTimeRequired") : t("form.pickupTime")}
            </Label>
            <Input
              id={`assign-time-${transfer.id}`}
              type="time"
              value={time}
              onChange={(event) => {
                setTime(event.target.value);
                setShowTimeError(false);
              }}
              className="h-8"
            />
            {showTimeError && (
              <p className="text-[11.5px] text-destructive">{t("form.timeRequiredError")}</p>
            )}
          </div>

          <div className="space-y-1">
            <div className="text-[10.5px] font-semibold uppercase tracking-[0.12em] text-text-dim">
              {t("board.drivers")}
            </div>
            {drivers.length === 0 ? (
              <p className="py-2 text-xs text-muted-foreground">
                {t("board.noActiveDrivers")}
              </p>
            ) : (
              <div className="max-h-56 space-y-0.5 overflow-y-auto">
                {drivers.map(({ driver, rideCount }) => {
                  const isCurrent = driver.id === transfer.driver_id;
                  const onShift = shiftCovers(driver, normalizePickupTime(time) ?? null);
                  return (
                    <button
                      key={driver.id}
                      type="button"
                      disabled={pendingId !== null || timeMissing}
                      onClick={() => submit(driver.id, driver.name)}
                      className={cn(
                        "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[13px] transition-colors",
                        "hover:bg-accent disabled:pointer-events-none disabled:opacity-50",
                        isCurrent && "bg-accent-tint-2 text-primary",
                      )}
                    >
                      {pendingId === driver.id ? (
                        <IconLoader2 className="size-3.5 shrink-0 animate-spin" />
                      ) : (
                        <IconUserCheck
                          className={cn(
                            "size-3.5 shrink-0",
                            isCurrent ? "text-primary" : "text-muted-foreground",
                          )}
                        />
                      )}
                      <span className="min-w-0 flex-1 truncate font-medium">
                        {driver.name}
                        {driver.vehicle && (
                          <span className="ml-1.5 font-normal text-muted-foreground">
                            {driver.vehicle}
                          </span>
                        )}
                      </span>
                      {onShift && (
                        <span className="shrink-0 rounded-full bg-green-100 px-1.5 py-0.5 text-[10px] font-semibold text-green-700 dark:bg-green-500/15 dark:text-green-300">
                          {t("car.onShift")}
                        </span>
                      )}
                      <span className="shrink-0 font-mono text-[11px] text-muted-foreground">
                        {t("board.rideCount", { count: rideCount })}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {transfer.driver_id !== null && (
            <Button
              variant="ghost"
              size="sm"
              disabled={pendingId !== null}
              onClick={() => submit(null)}
              className="h-7 w-full justify-start px-2 text-xs text-destructive hover:text-destructive"
            >
              {pendingId === "__unassign__" ? (
                <IconLoader2 className="size-3.5 animate-spin" />
              ) : (
                <IconCarOff className="size-3.5" />
              )}
              {t("actions.unassign")}
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
